import React, {
  createContext,
  useReducer,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as actionType from '../store/actions';
import { CONFIG } from '../config/constant';
import * as signalR from '@microsoft/signalr';
import { live } from '../config/server-config';
import moment from 'moment';
import { FaUser } from 'react-icons/fa';

const initialState = {
  ...CONFIG,
  isOpen: [],
  isTrigger: [],
  isChangePasswordOpen: false,
};

// connectionRef lives here at module level so createContext default includes it.
// This means useContext(ConfigContext).connectionRef is always a ref object,
// even before the Provider mounts.
const _defaultConnectionRef = { current: null };

const ConfigContext = createContext({ ...initialState, connectionRef: _defaultConnectionRef });
const { Provider } = ConfigContext;

const ConfigProvider = ({ children }) => {
  let trigger = [];
  let open = [];

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case actionType.PASSWORD_CHANGE:
        return { ...state, isChangePasswordOpen: action.payload };
      case actionType.BOX_LAYOUT:
        return { ...state, boxLayout: !state.boxLayout };
      case actionType.CHANGE_LAYOUT:
        return { ...state, layout: action.layout };
      case actionType.CHANGE_SUB_LAYOUT:
        return { ...state, subLayout: action.subLayout };
      case actionType.COLLAPSE_MENU:
        return { ...state, collapseMenu: !state.collapseMenu };
      case actionType.NAVIGATE_TAB_PROFILE:
        return { ...state, patientData: action.payload };
      case actionType.COLLAPSE_TOGGLE:
        if (action.menu.type === 'sub') {
          open = state.isOpen;
          trigger = state.isTrigger;
          const triggerIndex = trigger.indexOf(action.menu.id);
          if (triggerIndex > -1) {
            open = open.filter((item) => item !== action.menu.id);
            trigger = trigger.filter((item) => item !== action.menu.id);
          }
          if (triggerIndex === -1) {
            open = [...open, action.menu.id];
            trigger = [...trigger, action.menu.id];
          }
        } else {
          open = state.isOpen;
          const triggerIndex = state.isTrigger.indexOf(action.menu.id);
          trigger = triggerIndex === -1 ? [action.menu.id] : [];
          open = triggerIndex === -1 ? [action.menu.id] : [];
        }
        return { ...state, isOpen: open, isTrigger: trigger };
      case actionType.CONFIG_BLOCK:
        return { ...state, configBlock: !state.configBlock };
      case actionType.HEADER_BACK_COLOR:
        return { ...state, headerBackColor: action.headerBackColor };
      case actionType.HEADER_FIXED_LAYOUT:
        return {
          ...state,
          headerFixedLayout: !state.headerFixedLayout,
          headerBackColor:
            !state.headerFixedLayout && initialState.headerBackColor === ''
              ? 'header-blue'
              : state.headerBackColor,
        };
      case actionType.LAYOUT_TYPE:
        return {
          ...state,
          layoutType: action.layoutType,
          headerBackColor: action.headerBackColor ? action.headerBackColor : state.headerBackColor,
          navBackColor: action.navBackColor ? action.navBackColor : state.navBackColor,
          navBrandColor: action.navBrandColor ? action.navBrandColor : state.navBrandColor,
          navBackImage: action.layoutType === 'menu-light' ? false : state.navBackImage,
        };
      case actionType.LAYOUT6_BACKGROUND:
        return {
          ...state,
          layout6Background: action.layout6Background,
          layout6BackSize: action.layout6BackSize ? action.layout6BackSize : state.layout6BackSize,
        };
      case actionType.NAV_ACTIVE_LIST_COLOR:
        return { ...state, navActiveListColor: action.navActiveListColor };
      case actionType.NAV_BRAND_COLOR:
        return { ...state, navBrandColor: action.navBrandColor };
      case actionType.NAV_BACK_COLOR:
        return { ...state, navBackColor: action.navBackColor, navBackImage: initialState.navBackImage, navBrandColor: 'brand-default' };
      case actionType.NAV_BACK_IMAGE:
        return {
          ...state,
          layoutType: state.layoutType === 'menu-light' ? 'menu-dark' : state.layoutType,
          navBackImage: action.navBackImage,
          navBrandColor: '',
          navBackColor: '',
        };
      case actionType.NAV_COLLAPSE_LEAVE:
        if (action.menu.type === 'sub') {
          open = state.isOpen;
          trigger = state.isTrigger;
          const triggerIndex = trigger.indexOf(action.menu.id);
          if (triggerIndex > -1) {
            open = open.filter((item) => item !== action.menu.id);
            trigger = trigger.filter((item) => item !== action.menu.id);
          }
          return { ...state, isOpen: open, isTrigger: trigger };
        }
        return { ...state };
      case actionType.NAV_CONTENT_LEAVE:
        return { ...state, isOpen: open, isTrigger: trigger };
      case actionType.NAV_DROPDOWN_ICON:
        return { ...state, navDropdownIcon: action.navDropdownIcon };
      case actionType.NAV_FIXED_LAYOUT:
        return { ...state, navFixedLayout: !state.navFixedLayout };
      case actionType.NAV_ICON_COLOR:
        return { ...state, navIconColor: !state.navIconColor };
      case actionType.NAV_LIST_ICON:
        return { ...state, navListIcon: action.navListIcon };
      case actionType.NAV_LIST_TITLE_COLOR:
        return { ...state, navListTitleColor: action.navListTitleColor };
      case actionType.NAV_LIST_TITLE_HIDE:
        return { ...state, navListTitleHide: !state.navListTitleHide };
      case actionType.RTL_LAYOUT:
        return { ...state, rtlLayout: !state.rtlLayout };
      case actionType.RESET:
        return {
          ...state,
          layout: initialState.layout,
          subLayout: initialState.subLayout,
          collapseMenu: initialState.collapseMenu,
          layoutType: initialState.layoutType,
          navIconColor: initialState.navIconColor,
          headerBackColor: initialState.headerBackColor,
          navBackColor: initialState.navBackColor,
          navBrandColor: initialState.navBrandColor,
          navBackImage: initialState.navBackImage,
          rtlLayout: initialState.rtlLayout,
          navFixedLayout: initialState.navFixedLayout,
          headerFixedLayout: initialState.headerFixedLayout,
          boxLayout: initialState.boxLayout,
          navDropdownIcon: initialState.navDropdownIcon,
          navListIcon: initialState.navListIcon,
          navActiveListColor: initialState.navActiveListColor,
          navListTitleColor: initialState.navListTitleColor,
          navListTitleHide: initialState.navListTitleHide,
          layout6Background: initialState.layout6Background,
        };
      default:
        throw new Error();
    }
  }, initialState);

  // ── SignalR ─────────────────────────────────────────────────────────────────
  // connectionRef is exported via context so other components (e.g. WhatsApp tab)
  // can attach their own listeners WITHOUT creating a second connection.
  const connectionRef = useRef(null);
  const isLeaderRef = useRef(false);
  const channelRef = useRef(null);
  const tabId = useRef(`tab_${Math.random().toString(36).slice(2)}`);
  const heartbeatIntervalRef = useRef(null);

  const users = JSON.parse(localStorage.getItem('user_info'));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [incomingMessage, setIncomingMessage] = useState(null);

  useEffect(() => {
    const channel = new BroadcastChannel('signalr_channel');
    channelRef.current = channel;

    const tryBecomeLeader = () => {
      const now = Date.now();
      const currentLeader = localStorage.getItem('signalr_leader');
      const currentLeaderTab = localStorage.getItem('signalr_leader_tab');

      if (currentLeader && now - parseInt(currentLeader) < 5000) {
        console.log('Leader already exists, not trying to become leader');
        return;
      }

      localStorage.setItem('signalr_leader', now.toString());
      localStorage.setItem('signalr_leader_tab', tabId.current);

      setTimeout(() => {
        const latest = localStorage.getItem('signalr_leader');
        const latestTab = localStorage.getItem('signalr_leader_tab');
        if (latest === now.toString() && latestTab === tabId.current) {
          isLeaderRef.current = true;
          startConnection();
          startHeartbeat();
          console.log('This tab is now the SignalR leader');
        }
      }, 200);
    };

    const startHeartbeat = () => {
      heartbeatIntervalRef.current = setInterval(() => {
        if (isLeaderRef.current) {
          localStorage.setItem('signalr_leader', Date.now().toString());
          localStorage.setItem('signalr_leader_tab', tabId.current);
        }
      }, 3000);
    };

    const startConnection = () => {
      if (
        connectionRef.current &&
        connectionRef.current.state === signalR.HubConnectionState.Connected
      ) {
        return;
      }

      const baseDomain =
        live === false
          ? 'https://api-ldc.goldenbuzz.in/myhub'
          : 'https://api.sfd3973z443h.com/myhub';

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(baseDomain)
        .withAutomaticReconnect()
        .build();

      // ── IMPORTANT: assign to ref BEFORE .start() so WhatsApp tab can
      // access it immediately after connection is ready ──────────────────
      connectionRef.current = newConnection;

      newConnection
        .start()
        .then(() => {
          console.log('SignalR Connected');

          // Existing call-routing listener — untouched
          newConnection.on('ReceiveMessage', (user, message) => {
            console.log('Received Message', message);
            if (isLeaderRef.current) {
              channelRef.current?.postMessage({ user, message });
              handleProjectOpen(message);
            }
          });

          // NOTE: WaMessageReceived and WaStatusUpdated are NOT registered here.
          // They are registered inside WhatsAppChatTab so it can scope them to
          // the currently open lead and clean them up when the tab closes.
        })
        .catch((err) => console.error('SignalR Connection Error:', err));
    };

    const handleProjectOpen = async (message) => {
      if (!isLeaderRef.current) {
        console.log('Not leader, skipping message processing');
        return;
      }

      if (users.extensionNo === message.extensionNo) {
        const messageKey = `processed_message_${message.id}_${Date.now()}`;
        const existingProcess = localStorage.getItem(messageKey);
        if (existingProcess) {
          console.log('Message already being processed');
          return;
        }
        localStorage.setItem(messageKey, tabId.current);
        setTimeout(() => {
          if (localStorage.getItem(messageKey) === tabId.current) {
            localStorage.removeItem(messageKey);
          }
        }, 30000);

        const storageKey = `project_${message.id}_open`;
        await localStorage.getItem(storageKey);

        if (message.isExists) {
          setIncomingMessage(message);
          setShowConfirmModal(true);
        } else {
          const url =
            live === false
              ? 'https://ldc.goldenbuzz.in'
              : 'https://sfd3973z443h.com';

          localStorage.setItem('new_lead_mobile', message.mobile);
          localStorage.setItem('from_signalr_lead', 'true');

          const addLeadUrl = `${url}/lead-management`;
          const newTab = window.open(addLeadUrl, '_blank');
          if (!newTab) console.error('Popup blocked');
        }
      }
    };

    // Leadership monitor
    const leadershipCheck = setInterval(() => {
      const currentLeader = localStorage.getItem('signalr_leader');
      const currentLeaderTab = localStorage.getItem('signalr_leader_tab');
      const now = Date.now();

      if (!currentLeader || now - parseInt(currentLeader) > 10000) {
        if (!isLeaderRef.current) {
          console.log('No active leader found, trying to become leader');
          tryBecomeLeader();
        }
      } else if (isLeaderRef.current && currentLeaderTab !== tabId.current) {
        console.log('Another tab is leader, stepping down');
        isLeaderRef.current = false;
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      }
    }, 5000);

    tryBecomeLeader();

    return () => {
      if (connectionRef.current) connectionRef.current.stop();
      channel.close();
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      clearInterval(leadershipCheck);
      if (isLeaderRef.current) {
        localStorage.removeItem('signalr_leader');
        localStorage.removeItem('signalr_leader_tab');
      }
    };
  }, []);

  const handleConfirmOpenTab = () => {
    const message = incomingMessage;
    if (!message) return;

    const storageKey = `project_${message.id}_open`;
    const projectUrl = `${message.url}/${message.id}`;
    const newTab = window.open(projectUrl, '_blank');

    if (newTab) {
      localStorage.setItem(storageKey, tabId.current);
      const interval = setInterval(() => {
        if (newTab.closed) {
          clearInterval(interval);
          if (localStorage.getItem(storageKey) === tabId.current) {
            localStorage.removeItem(storageKey);
          }
        }
      }, 1000);
    } else {
      console.error('Popup blocked');
    }

    setShowConfirmModal(false);
    setIncomingMessage(null);
  };

  return (
    // ── connectionRef is passed through context so any child component
    // (e.g. WhatsAppChatTab) can attach listeners without a second hub connection.
    <Provider value={{ state, dispatch, connectionRef }}>
      {children}
      {showConfirmModal && incomingMessage && (
        <div className='glass-modal-overlay'>
          <div className='glass-modal'>
            <div className='glass-modal-header'>
              <div className='glass-modal-icon'>
                <FaUser />
              </div>
              <h2>Confirm details to open profile</h2>
            </div>

            <div className='glass-modal-content'>
              <div className='glass-row'>
                <span>Name :</span>
                <h5>{incomingMessage.fullName || 'N/A'}</h5>
              </div>
              <div className='glass-row'>
                <span>Date of birth :</span>
                <h5>
                  {incomingMessage?.dob
                    ? moment(incomingMessage.dob).format('DD/MM/YYYY')
                    : 'N/A'}
                </h5>
              </div>
              <div className='glass-row'>
                <span>First line of address :</span>
                <h5>{incomingMessage.address || 'N/A'}</h5>
              </div>
              <div className='glass-row'>
                <span>Incoming call :</span>
                <h5>{incomingMessage.mobile || 'N/A'}</h5>
              </div>
            </div>

            <div className='glass-modal-actions'>
              <button
                className='glass-btn glass-btn-primary'
                onClick={handleConfirmOpenTab}
              >
                Yes
              </button>
              <button
                className='glass-btn glass-btn-danger'
                onClick={() => setShowConfirmModal(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </Provider>
  );
};

export { ConfigContext, ConfigProvider };